import axios, { AxiosInstance, AxiosRequestConfig, AxiosError, isAxiosError } from 'axios'
import { storyDB } from './db'
import { authService } from './auth'

interface QueuedRequest {
  method: string
  url: string
  data?: any
  headers?: Record<string, string>
}

class ApiService {
  private client: AxiosInstance
  private isOnline: boolean = navigator.onLine

  constructor() {
    this.client = axios.create({
      baseURL: import.meta.env.VITE_API_URL || 'http://localhost:8000',
      timeout: parseInt(import.meta.env.VITE_API_TIMEOUT || '30000'),
      headers: {
        'Content-Type': 'application/json',
      },
    })

    // Request interceptor
    this.client.interceptors.request.use(
      async (config) => {
        // Add Firebase auth token if user is authenticated
        const token = await authService.getIdToken()
        if (token) {
          config.headers.Authorization = `Bearer ${token}`
        }
        return config
      },
      (error) => Promise.reject(error)
    )

    // Response interceptor
    this.client.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        if (!error.response || error.response.status === 0) {
          // Network error - add to offline queue
          await this.handleOfflineRequest(error.config!)
          return Promise.reject(new Error('Request queued for offline sync'))
        }

        // Handle specific error cases
        if (error.response?.status === 401) {
          // Check if this is the onboarding check for a new user
          const isOnboardingCheck = error.config?.url?.includes('/users/me')
          const isNewUser = !localStorage.getItem('user_exists_in_backend')

          if (isOnboardingCheck && isNewUser) {
            // Don't sign out for new users checking their profile
          } else {
            // Unauthorized - sign out from Firebase
            try {
              await authService.signOut()
            } catch (signOutError) {
              //console.error('Error signing out:', signOutError)
            }
            window.location.href = '/auth/login'
          }
        }

        return Promise.reject(error)
      }
    )

    // Listen for online/offline events
    window.addEventListener('online', () => {
      this.isOnline = true
      this.syncOfflineQueue()
    })

    window.addEventListener('offline', () => {
      this.isOnline = false
    })
  }

  private async handleOfflineRequest(config: AxiosRequestConfig) {
    const queueItem = {
      type: 'create' as const,
      endpoint: config.url!,
      data: {
        method: config.method,
        url: config.url,
        data: config.data,
        headers: config.headers,
      },
      timestamp: new Date(),
      retries: 0,
    }

    await storyDB.addToQueue(queueItem)
  }

  async syncOfflineQueue() {
    if (!this.isOnline) return

    const queueItems = await storyDB.getQueueItems()

    for (const item of queueItems) {
      try {
        const { method, url, data, headers } = item.data as QueuedRequest

        await this.client.request({
          method,
          url,
          data,
          headers,
        })

        // Success - remove from queue
        await storyDB.removeFromQueue(item.id)
      } catch (error) {
        // Update retry count
        await storyDB.updateQueueItem(item.id, {
          retries: item.retries + 1,
        })

        // If too many retries, remove from queue
        if (item.retries >= 3) {
          await storyDB.removeFromQueue(item.id)
          //console.error('Failed to sync offline request after 3 retries:', error)
        }
      }
    }
  }

  // All legacy profile/story/media/character/history endpoints removed for MVP

  // User endpoints
  async createUser(data: { uid: string; email: string; display_name?: string }) {
    try {
      const response = await this.client.post('/users', data)

      // Mark that user exists in backend
      localStorage.setItem('user_exists_in_backend', 'true')

      return response.data
    } catch (error) {
      //console.error('API: User creation failed:', error)
      throw error
    }
  }

  async getCurrentUser() {
    try {
      const response = await this.client.get('/users/me')

      // Mark that user exists in backend
      localStorage.setItem('user_exists_in_backend', 'true')

      return response.data
    } catch (error) {
      //console.error('API: Failed to get user profile:', error)

      // If user not found, they're likely a new user
      if ((error as any)?.response?.status === 404) {
        localStorage.removeItem('user_exists_in_backend')
      }

      throw error
    }
  }

  // Onboarding endpoint
  async submitOnboarding(data: any) {
    try {
      const response = await this.client.post('/users/onboarding', data)
      return response.data
    } catch (error) {
      //console.error('API: Onboarding failed:', error)
      if (isAxiosError(error)) {
        //console.error('API: Response status:', error?.code)
        //console.error('API: Response data:', error?.response?.data?.message || error.message)
      }
      throw error
    }
  }

  // Incremental onboarding: create child
  async createChild(data: {
    name: string
    birth_year: string
    selected_conditions?: string[]
    avatar_url?: string | null
  }) {
    try {
      const response = await this.client.post('/users/me/children', data)
      return response.data as { id: string }
    } catch (error) {
      //console.error('API: Create child failed:', error)
      throw error
    }
  }

  // Incremental onboarding: update child
  async updateChild(
    childId: string,
    data: Partial<{
      name: string
      birth_year: string
      selected_conditions: string[]
      avatar_url: string | null
    }>
  ) {
    try {
      const response = await this.client.put(`/users/me/children/${childId}`, data)
      return response.data
    } catch (error) {
      //console.error('API: Update child failed:', error)
      throw error
    }
  }

  // Update current user (preferred_language only for now)
  async updateCurrentUser(data: Partial<{ preferred_language: string }>) {
    try {
      const response = await this.client.put('/users/me', data)
      return response.data
    } catch (error) {
      //console.error('API: Update current user failed:', error)
      throw error
    }
  }

  // Avatar upload endpoint
  async uploadChildAvatar(childId: string, data: any) {
    try {
      const response = await this.client.post(`/users/me/children/${childId}/avatar`, data)
      return response.data
    } catch (error) {
      //console.error('API: Avatar upload failed:', error)

      if (isAxiosError(error)) {
        //console.error('API: Response status:', error?.code)
        //console.error('API: Response data:', error?.response?.data?.message || error.message)
      }
      throw error
    }
  }

  // Avatar preview generation (Flux) - unauthenticated during onboarding
  async generateAvatarPreview(imageData: string, childName: string) {
    try {
      const response = await this.client.post('/features/generate-avatar-preview', {
        image_data: imageData,
        child_name: childName || 'child',
      })
      return response.data as {
        generated_avatar_url: string
        flux_request_id?: string
        status: string
      }
    } catch (error) {
      //console.error('API: Avatar preview generation failed:', error)
      if ((error as any).response) {
        //console.error('API: Response status:', (error as any).response.status)
        //console.error('API: Response data:', (error as any).response.data)
      }
      throw error
    }
  }

  // Utility methods (none for MVP)

  // Superpower endpoints
  // Superpower endpoints removed

  // Health check
  async healthCheck(): Promise<boolean> {
    try {
      await this.client.get('/health')
      return true
    } catch {
      return false
    }
  }

  // --- Story Generation Flow ---

  async createProfileSession(data: { language: string; neurodivergence?: string }) {
    try {
      const response = await this.client.post('/characters/session/start', data)
      return response.data as {
        id: string
        language: string
        neurodivergence?: string
        created_at: string
      }
    } catch (error) {
      //console.error('API: Create session failed:', error)
      throw error
    }
  }

  async createCharacter(data: {
    name: string
    character_type: string
    detail?: string
    style: string
    avatar_image?: string
    is_child: boolean
    session_id: string
  }) {
    try {
      const response = await this.client.post('/characters/', data)
      return response.data
    } catch (error) {
      //console.error('API: Create character failed:', error)
      throw error
    }
  }

  async generateStory(data: {
    session_id: string
    theme_type: string
    story_seed?: string
    image_style: string
    main_character_id?: number
  }) {
    try {
      const response = await this.client.post('/stories/v3/generate', data)
      return response.data as {
        id: number
        session_id: string
        theme: string
        content: string
        created_at: string
        word_count?: number
        characters?: any[]
      }
    } catch (error) {
      //console.error('API: Story generation failed:', error)
      throw error
    }
  }
}

export const api = new ApiService()
export default api
